import { Grid } from "@material-ui/core"
import { useStyles } from "./roadmap-list-styles"
import { useCallback, useEffect, useMemo, memo, useState } from "react"
import { VotacoesModel } from "model/api/gestao/votacao-desenvolvimento/votacoes-model"
import { useGetVotacoes } from "data/api/gestao/clickup/get-votacoes"
import { useCadastros, useSessaoAtual, useToastSaurus } from "services/app"
import { CircularLoading } from "views/components"
import { usePostVotacoesByIdVoto } from "data/api/gestao/clickup/post-votacoes-by-id-voto"
import { VotoModel } from "model/api/gestao/votacao-desenvolvimento/voto-model"
import { RoadmapListData } from "./roadmap-list-data"
import classNames from "classnames"
import { useDefaultCadastroStyles } from "views/pages/private/cadastros/components/default-cadastro-styles"
import { RoadmapPeriodo } from "./components/roadmap-data/roadmap-periodo"
import { isEmpty } from "lodash"

export interface VotacoesDesenvolvimentoListProps {
    sistemaSelected: string
}

const RoadmapListComponent = ({ sistemaSelected }: VotacoesDesenvolvimentoListProps) => {
    const [votacoes, setVotacoes] = useState<VotacoesModel[]>([])

    const cadastroClasses = useDefaultCadastroStyles();
    const classes = useStyles()
    const { getVotacoes, carregando: carregandoVotacoes } = useGetVotacoes()
    const { postVotacoesByIdVoto, carregando: carregandoVoto } = usePostVotacoesByIdVoto()
    const { usuario } = useSessaoAtual()
    const { showToast } = useToastSaurus()
    const { abrirDialogTarefaDetalhes } = useCadastros()
    const [carregandoInterno, setCarregandoInterno] = useState(false);

    // Memoizar o estado de carregamento para evitar recálculo em cada renderização
    const carregando = useMemo(() =>
        [carregandoVotacoes, carregandoVoto, carregandoInterno].some(Boolean)
        , [carregandoVotacoes, carregandoVoto, carregandoInterno]);

    const handleGetVotacoes = useCallback(async () => {
        setCarregandoInterno(true);
        try {
            const res = await getVotacoes(usuario?.NomeComercial ?? '')
            if (res.erro) throw res.erro

            const response = res.resultado?.data as VotacoesModel[]
            if (isEmpty(response)) {
                showToast('info', 'Nenhuma votação encontrada para o usuário atual.')
                return;
            }

            setVotacoes(response.map(votacao => ({
                ...votacao,
                porcentagem: Math.floor(Math.random() * 101) // Gera um número aleatório entre 0 e 100
            })))
        } catch (err: any) {
            showToast('error', 'Erro ao buscar votações. Detalhe: ' + err.message)
        } finally {
            setCarregandoInterno(false);
        }
    }, [getVotacoes, showToast, usuario?.NomeComercial])

    const handleClickVote = useCallback(async (id: string, tarefaId: string) => {
        try {
            const votoModel: VotoModel = {
                id: id,
                votacaoTarefaId: tarefaId,
                usuario: usuario?.NomeComercial ?? ''
            }
            const res = await postVotacoesByIdVoto(votoModel, id)
            if (res.erro) throw res.erro

            setVotacoes(prev => {
                const index = prev.findIndex(votacao => votacao.id === id)
                const voto = prev[index]
                if (index !== -1) {
                    if (voto.votado) {
                        const updatedVotacao = { ...prev[index], votado: false, votos: prev[index].votos - 1 }
                        showToast('info', 'Votação retirada com sucesso.')
                        return [...prev.slice(0, index), updatedVotacao, ...prev.slice(index + 1)]
                    }
                    const updatedVotacao = { ...prev[index], votado: true, votos: prev[index].votos + 1 }
                    showToast('success', 'Voto computado com sucesso.')
                    return [...prev.slice(0, index), updatedVotacao, ...prev.slice(index + 1)]
                }
                return prev
            })
        } catch (err: any) {
            showToast('error', 'Erro ao computar voto. Detalhe: ' + err.message)
            throw err
        }
    }, [postVotacoesByIdVoto, showToast, usuario?.NomeComercial])

    const handleClickLink = useCallback((link: string) => {
        // Abrir o link em uma nova aba do navegador
        if (link) {
            const newWindow = window.open(link, '_blank');
            // Garante que o foco vá para a nova aba (alguns navegadores bloqueiam isso por segurança)
            if (newWindow) newWindow.focus();
        }
    }, []);

    const handleClickCard = useCallback((model: VotacoesModel, desabilitado: boolean) => {
        abrirDialogTarefaDetalhes(model, handleClickLink, handleClickVote, desabilitado)
    }, [abrirDialogTarefaDetalhes, handleClickLink, handleClickVote]);

    useEffect(() => {
        handleGetVotacoes()
    }, [handleGetVotacoes])

    return (
        <>
            <Grid item xs={12} sm={12} md={12} className={classNames(classes.list, cadastroClasses.list)}>
                {carregando && <CircularLoading tipo="FULLSIZED" />}
                <Grid className={classes.defaultContainer}>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <RoadmapPeriodo />
                        </Grid>
                    </Grid>
                    <RoadmapListData
                        {...{
                            votacoes,
                            sistemaSelected,
                            carregando,
                            handleClickCard,
                            handleClickLink,
                            handleClickVote,
                        }}
                    />
                </Grid>
            </Grid>
        </>
    )
}

RoadmapListComponent.displayName = 'VotacoesDesenvolvimentoList';

// Memoizar o componente inteiro para prevenir re-renderizações desnecessárias
export const RoadmapList = memo(RoadmapListComponent);